import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { CheckCircle, EventAvailable } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ThemedPaper } from '@/components/ThemedPaper';
import { FormattedText } from '@/components/FormattedText';
import { useReservePath } from '@/hooks/useDomain';
import { useEmbed } from '@/context/EmbedContext';
import type { LocalizedTextDto } from '@/api/generated';
import { getWidgetLang, pickLocalizedText } from '@/utils/localizedText';

interface OutletContext {
  storeInfo?: {
    widget_settings?: {
      redirect_url?: string | null;
      confirmation_message?: LocalizedTextDto | null;
    };
  };
}

/**
 * Success screen after reservation is completed
 * Redirects to redirect_url after 3 seconds if configured
 */
export function ThankYouScreen() {
  const navigate = useNavigate();
  const reservePath = useReservePath();
  const { t, i18n } = useTranslation();
  const { storeInfo } = useOutletContext<OutletContext>();
  const { isEmbedded } = useEmbed();
  const [countdown, setCountdown] = useState(3);

  const redirectUrl = storeInfo?.widget_settings?.redirect_url;
  const confirmationMessage = pickLocalizedText(
    storeInfo?.widget_settings?.confirmation_message,
    getWidgetLang(i18n.language),
  );

  // Handle redirect if redirect_url is configured
  useEffect(() => {
    if (!redirectUrl) return;

    // Start countdown
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);

          if (isEmbedded) {
            // When embedded, notify parent instead of redirecting iframe
            window.parent.postMessage(
              {
                type: 'sala-widget-complete',
                redirectUrl: redirectUrl,
              },
              '*'
            );
          } else {
            // Normal mode: perform redirect
            window.location.href = redirectUrl;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [redirectUrl, isEmbedded]);

  const handleMakeAnother = () => {
    navigate(reservePath);
  };

  return (
    <ThemedPaper>
      <Box p={4} textAlign="center">
        <Box mb={3}>
          <CheckCircle color="success" sx={{ fontSize: 80 }} />
        </Box>

        <Typography variant="h4" component="h2" gutterBottom>
          {t('thankYouScreen.title')}
        </Typography>

        {confirmationMessage ? (
          <Box mb={2}>
            <FormattedText text={confirmationMessage} variant="body1" color="text.secondary" />
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary" paragraph>
            {t('thankYouScreen.message')}
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary" paragraph>
          {t('thankYouScreen.lookingForward')}
        </Typography>

        {redirectUrl && (
          <Typography variant="body2" color="primary" fontWeight="bold" paragraph>
            {t('thankYouScreen.redirecting', { count: countdown })}
          </Typography>
        )}

        <Box mt={4}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleMakeAnother}
            startIcon={<EventAvailable />}
            disabled={!!redirectUrl}
          >
            {t('thankYouScreen.makeAnother')}
          </Button>
        </Box>
      </Box>
    </ThemedPaper>
  );
}
